import AI_ICON from "@assets/quick-news/ai-icon.svg";
import CHEVRON_LEFT from "@assets/quick-news/Chevron_left_grey.svg";
import { useBHCustomEventAnalyticsCall } from "@hooks/useAnalyticsCall/useBHCustomEventAnalyticsCall";
import { QNCardType } from "@typings/QuickNews";
import Cookies from "js-cookie";
import { ReactElement, useCallback } from "react";
import { twMerge } from "tailwind-merge";

import { QnHelpBtn } from "./QnHelpBtn";

type QNTopNavProps = {
  activeTab: QNCardType;
  setActiveTab: (tab: QNCardType) => void;
};

const TABS: QNCardType[] = ["MyFeed", "Videos"];

// Navigation button styles
const NAV_BUTTON_STYLES = {
  base: "relative z-10 h-full flex-1 text-2sm font-medium text-neutral-300 py-1 transition-all duration-800 overflow-hidden",
  underline:
    "before:absolute before:bottom-0 before:bg-bg-primary before:transition-all before:duration-800 before:ease-linear before:h-0.5",
} as const;

// AI Icon styles
const AI_ICON_STYLES = {
  base: "inline-block relative top-[-8px]",
  active: "opacity-100",
  inactive: "opacity-30",
} as const;

export default function QnTopNav({
  activeTab,
  setActiveTab,
}: QNTopNavProps): ReactElement {
  const BHCustomEvent = useBHCustomEventAnalyticsCall();

  const goBackClick = useCallback(() => {
    const qnRedirectUrl = Cookies.get("qnRedirectUrl");

    if (qnRedirectUrl) {
      window.location.href = decodeURIComponent(qnRedirectUrl);
    } else {
      window.location.href = "/";
    }

    BHCustomEvent("quick-news", "back", "home", undefined, "");
  }, [BHCustomEvent]);

  const handleTabClick = useCallback(
    (tab: QNCardType) => {
      setActiveTab(tab);
    },
    [setActiveTab],
  );

  const renderTabButton = useCallback(
    (tab: QNCardType) => {
      const isMyFeed = tab === "MyFeed";
      const isActive = activeTab === tab;

      return (
        <button
          key={tab}
          className={twMerge(
            NAV_BUTTON_STYLES.base,
            NAV_BUTTON_STYLES.underline,
            isMyFeed
              ? "before:right-0 before:origin-right"
              : "before:left-0 before:origin-left",
            isActive
              ? "font-bold text-text-inverse before:w-full"
              : "text-gray-1100 before:w-0",
          )}
          onClick={() => {
            handleTabClick(tab);
          }}
          aria-label={`Switch to ${tab} tab`}
        >
          <div className="inline-block">{tab}</div>
          {isMyFeed && (
            <div
              className={twMerge(
                AI_ICON_STYLES.base,
                isActive ? AI_ICON_STYLES.active : AI_ICON_STYLES.inactive,
              )}
            >
              <img src={AI_ICON} alt="AI Icon" className="ml-1 w-[11px]" />
            </div>
          )}
        </button>
      );
    },
    [activeTab, handleTabClick],
  );

  return (
    <header className="qn-header z-40 w-full h-[45px] md:mt-10 top-0 bg-transparent px-[15px]">
      <div className="relative mx-auto w-full flex items-center justify-center gap-2">
        <button
          aria-label="Go back"
          onClick={goBackClick}
          className="md:absolute left-4 w-[45px] p-2"
        >
          <img height={24} width={24} alt="Go back" src={CHEVRON_LEFT} />
        </button>

        <nav className="qn-header-nav flex items-center justify-center h-[45px] w-[calc(100%-45px)] md:w-[450px] my-1 border-b border-gray-1100">
          {TABS.map(renderTabButton)}
        </nav>

        <QnHelpBtn />
      </div>
    </header>
  );
}
